import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { map } from "rxjs/operators";
import { UtilityService } from "./utility.service";
import { Router } from "@angular/router";
import { LocalStorageService } from "./local-storage.service";
import { NgxSpinnerService } from "ngx-spinner";
import { v4 as uuidv4 } from "uuid";
import { ApiService } from "./api.service";

@Injectable({
  providedIn: "root",
})
export class HttpRequestsService {
  private hostUrl = environment.apiEndPoint;
  private isAutoReportError = true;

  constructor(
    public http: HttpClient,
    private router: Router,
    private apiService: ApiService,
    public utility: UtilityService,
    private localStorage: LocalStorageService,
    private spinner: NgxSpinnerService,
  ) {}

  public setAutoReportError(isAuto: boolean) {
    this.isAutoReportError = isAuto;
  }

  private getContentType(body: any): string {
    if (body instanceof FormData) {
      return "multipart/form-data";
    }
    return "application/json";
  }

  /**
   * getHeader(headerOptions, doNotSendAuthorizationParam) => set header option with authorization token base on param
   * @param headerOptions in headeroption
   * @param doNotSendAuthorizationParam in authorization sent or not
   */
  getHeader(
    headerOptions: any = {},
    doNotSendAuthorizationParam: boolean = false,
    contentType: string = "application/json",
  ) {
    const headerParams: any = {
      "Accept-Version": "v1",
      "Accept-Language": "en",
      "Access-Control-Allow-Origin": "*",
      "x-time-zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      "x-idempotent-key": uuidv4(),
    };

    if (!doNotSendAuthorizationParam) {
      headerParams["Authorization"] = `Bearer ${this.apiService.token}`;
    }

    if (contentType !== "multipart/form-data") {
      headerParams["Content-Type"] = contentType;
    }

    // Add restaurant ID header if available
    const restaurantId = this.localStorage.getLocalStore(
      "CURRENT_RESTAURANT_ID",
    );
    if (restaurantId) {
      headerParams["x-restaurant-id"] = restaurantId;
    }

    if (headerOptions) {
      Object.assign(headerParams, headerOptions);
    }

    const headers = new HttpHeaders(headerParams);
    return { headers };
  }

  /**
   * post(url, body, doNotSendAuthorizationParam?, headerOptions?) => post method base on params
   * @param url in url
   * @param body in body param
   * @param doNotSendAuthorizationParam in authorization sent or not
   * @param headerOptions in header option
   */
  post<T = any>(
    url: string,
    body: any,
    doNotSendAuthorizationParam: boolean = false,
    headerOptions: any = {},
    autoCheck400Error = this.isAutoReportError,
  ) {
    return this.rawPost<T>(
      this.hostUrl,
      url,
      body,
      doNotSendAuthorizationParam,
      headerOptions,
      autoCheck400Error,
    );
  }

  rawPost<Return>(
    host: string,
    url: string,
    body: any,
    doNotSendAuthorizationParam: boolean = false,
    headerOptions: any = {},
    autoCheck400Error: boolean = this.isAutoReportError,
  ): Promise<Return> {
    return new Promise<Return>((resolve, reject) => {
      const contentType = this.getContentType(body);
      const options = this.getHeader(
        headerOptions,
        doNotSendAuthorizationParam,
        contentType,
      );

      this.http
        .post(`${host}${url}`, body, options)
        .pipe(map((res) => res))
        .subscribe(
          (res) => {
            resolve(res as Return);
          },
          (err) => {
            if (autoCheck400Error) {
              this.handleError(err, reject);
            } else {
              reject(err);
            }
          },
        );
    });
  }

  rawGet<T = any>(
    host: string,
    url: string,
    doNotSendAuthorizationParam: boolean = false,
    headerOptions: any = {},
    params: Record<string, any> = {},
    autoCheck400Error: boolean = true,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const options = this.getHeader(
        headerOptions,
        doNotSendAuthorizationParam,
      );
      this.http
        .get(`${host}${url}`, { ...options, params })
        .pipe(map((res) => res))
        .subscribe(
          (res) => {
            resolve(res as T);
          },
          (err) => {
            if (autoCheck400Error) {
              this.handleError(err, reject);
            } else {
              reject(err);
            }
          },
        );
    });
  }
  /**
   * get(url, doNotSendAuthorizationParam?, headerOptions?) => get method base on params
   * @param url in url
   * @param doNotSendAuthorizationParam in authorization sent or not
   * @param headerOptions in header option
   */
  get(
    url: string,
    doNotSendAuthorizationParam: boolean = false,
    headerOptions: any = {},
  ) {
    return this.rawGet(
      this.hostUrl,
      url,
      doNotSendAuthorizationParam,
      headerOptions,
    );
  }

  /**
   * get(url, doNotSendAuthorizationParam?, headerOptions?) => get method base on params
   * @param url in url
   * @param doNotSendAuthorizationParam in authorization sent or not
   * @param headerOptions in header option
   */
  getNoURL(
    url: string,
    doNotSendAuthorizationParam: boolean = false,
    headerOptions: any = {},
  ) {
    return new Promise((resolve, reject) => {
      this.http
        .get(`${url}`)
        .pipe(map((res) => res))
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => {
            this.handleError(err, reject);
          },
        );
    });
  }

  /**
   * put(url, body, doNotSendAuthorizationParam?, headerOptions?) => put method base on params
   * @param url in url
   * @param body in body param
   * @param doNotSendAuthorizationParam in authorization sent or not
   * @param headerOptions in header option
   */
  put(
    url: string,
    body: any,
    headerOptions: any = {},
    doNotSendAuthorizationParam: boolean = false,
  ) {
    return this.rawPut(
      this.hostUrl,
      url,
      body,
      headerOptions,
      doNotSendAuthorizationParam,
    );
  }

  rawPut<T = any>(
    host: string,
    url: string,
    body: any,
    headerOptions: any = {},
    doNotSendAuthorizationParam: boolean = false,
    autoCheck400Error: boolean = this.isAutoReportError,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const contentType = this.getContentType(body);
      const options = this.getHeader(
        headerOptions,
        doNotSendAuthorizationParam,
        contentType,
      );

      this.http
        .put(`${host}${url}`, body, options)
        .pipe(map((res) => res))
        .subscribe(
          (res) => {
            resolve(res as T);
          },
          (err) => {
            if (autoCheck400Error) {
              this.handleError(err, reject);
            } else {
              reject(err);
            }
          },
        );
    });
  }

  rawPatch<T = any>(
    host: string,
    url: string,
    body: any,
    headerOptions: any = {},
    doNotSendAuthorizationParam: boolean = false,
    autoCheck400Error: boolean = this.isAutoReportError,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const contentType = this.getContentType(body);
      const options = this.getHeader(
        headerOptions,
        doNotSendAuthorizationParam,
        contentType,
      );
      this.http
        .patch(`${host}${url}`, body, options)
        .pipe(map((res) => res))
        .subscribe(
          (res) => {
            resolve(res as T);
          },
          (err) => {
            if (autoCheck400Error) {
              this.handleError(err, reject);
            } else {
              reject(err);
            }
          },
        );
    });
  }

  /**
   * delete(url, doNotSendAuthorizationParam?, headerOptions?) => delete method base on params
   * @param url in url
   * @param doNotSendAuthorizationParam in authorization sent or not
   * @param headerOptions in header option
   */
  delete(
    url: string,
    headerOptions: any = {},
    doNotSendAuthorizationParam: boolean = false,
  ) {
    return new Promise((resolve, reject) => {
      const options = this.getHeader(
        headerOptions,
        doNotSendAuthorizationParam,
      );
      this.http
        .delete(`${this.hostUrl}${url}`, options)
        .pipe(
          map((res) => {
            return res;
          }),
        )
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => {
            this.handleError(err, reject);
          },
        );
    });
  }

  rawDelete(
    host: string,
    url: string,
    headerOptions: any = {},
    doNotSendAuthorizationParam: boolean = false,
  ) {
    return new Promise((resolve, reject) => {
      const options = this.getHeader(
        headerOptions,
        doNotSendAuthorizationParam,
      );
      this.http
        .delete(`${host}${url}`, options)
        .pipe(
          map((res) => {
            return res;
          }),
        )
        .subscribe(
          (res) => {
            resolve(res);
          },
          (err) => {
            this.handleError(err, reject);
          },
        );
    });
  }

  /**
   * handleError(err) => handle error message
   * @param err in arr object
   */
  handleError(err, reject) {
    if (err.status === 400) {
      const error =
        err.error.error || err.error.message
          ? err.error.message || err.error.error
          : "Wrong credentials provided. Please check your email and password";
      this.utility.showErrorToast(error);
      this.utility.hideLoading();
      this.spinner.hide();
    } else if (err.status === 401) {
      const error =
        err.error.error || err.error.message
          ? err.error.message || err.error.error
          : "Session Expired!";
      this.utility.showErrorToast(error);
      this.utility.hideLoading();
      this.spinner.hide();
      this.utility.clearStorage();
      this.localStorage.clearDataFromIndexedDBWIthSafePhoneCode();
      this.router.navigate(["/login"]);
    } else if (err.status === 500) {
      //const error = err.error.error ? err.error.error : 'Internal server error!';
      const error =
        "Oops! Something went wrong on our end. Please don't worry, " +
        "and kindly reach out to our customer service for assistance.";
      this.utility.showErrorToast(error);
      this.spinner.hide();
      this.utility.hideLoading();
      reject(error);
    } else {
      reject(err);
    }
  }

  uploadWithProgress(
    url: string,
    body: any,
    doNotSendAuthorizationParam: boolean = false,
    headerOptions: any = {},
  ) {
    const options = this.getHeader(headerOptions, doNotSendAuthorizationParam);
    return this.http
      .post(`${this.hostUrl}${url}`, body, {
        ...options,
        reportProgress: true,
        observe: "events",
      })
      .pipe();
  }
}
